import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, Check, X, Upload, Loader2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_client-360-pro/artifacts/t5motboh_wertia%20perfect.png";

/** Public mobile upload page — opened by scanning the QR shown on desktop. No auth. */
export default function MobileUploadPage() {
  const { sessionId } = useParams();
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | uploading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [progress, setProgress] = useState(0);
  const [purpose, setPurpose] = useState("identity");

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  // Fetch session metadata to know the purpose
  React.useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiBase}/api/m/upload-sessions/${sessionId}`);
        setPurpose(res.data.purpose || "identity");
        if (res.data.status === "uploaded") setStatus("success");
      } catch {
        setStatus("error");
        setErrorMsg("Sitzung ungültig oder abgelaufen");
      }
    })();
  }, [apiBase, sessionId]);

  const isProfile = purpose === "profile_photo";

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setStatus("idle");
  };

  const upload = async () => {
    if (!file) return;
    setStatus("uploading");
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${apiBase}/api/m/upload-sessions/${sessionId}/file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setStatus("success");
    } catch (e) {
      setStatus("error");
      setErrorMsg(e.response?.data?.detail || "Fehler upload");
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setStatus("idle");
    setProgress(0);
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="mobile-upload-page">
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/10 flex items-center justify-center">
        <img src={LOGO_URL} alt="Wertia Finanz" className="h-10" />
      </div>

      <div className="flex-1 px-5 py-6 flex flex-col">
        {status === "success" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
            <div className="w-20 h-20 rounded-full bg-green-600 flex items-center justify-center">
              <Check className="w-10 h-10" strokeWidth={2.5} />
            </div>
            <h2 className="font-serif text-3xl font-bold">Fertig!</h2>
            <p className="text-white/70 max-w-xs">
              Dokument hochgeladen correttamente. Torna sul desktop: l'IA sta già analizzando il Dokument.
            </p>
            <button
              onClick={reset}
              className="mt-4 text-sm text-white/60 underline"
              data-testid="upload-another-btn"
            >
              Hochladen un altro
            </button>
          </div>
        ) : status === "error" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
            <div className="w-20 h-20 rounded-full bg-red-600 flex items-center justify-center">
              <X className="w-10 h-10" strokeWidth={2.5} />
            </div>
            <h2 className="font-serif text-2xl font-bold">Fehler</h2>
            <p className="text-white/70 max-w-xs">{errorMsg}</p>
            <button
              onClick={reset}
              className="mt-4 px-4 py-2 bg-white text-black rounded font-medium text-sm"
              data-testid="retry-btn"
            >
              Riprova
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="text-xs uppercase tracking-wider text-red-500 font-bold">
                {isProfile ? "Profilfoto" : "Scansione Dokument"}
              </div>
              <h1 className="font-serif text-3xl font-bold mt-1">
                {isProfile ? "La tua foto" : "Identitätskarte"}
              </h1>
              <p className="text-sm text-white/60 mt-2 max-w-sm mx-auto">
                {isProfile
                  ? "Wählen Sie ein Foto aus dem Album oder nehmen Sie ein neues auf. Es wird Ihr Profilbild im CRM."
                  : "Inquadra il Dokument e scatta una foto. Verrà gesendet automaticamente al CRM."}
              </p>
            </div>

            {preview ? (
              <div className="prime-card overflow-hidden rounded-xl mb-4 border border-white/10">
                <img src={preview} alt="preview" className="w-full max-h-[60vh] object-contain bg-black" />
              </div>
            ) : (
              <label
                htmlFor="cam-input"
                className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-white/30 rounded-xl py-12 px-6 cursor-pointer hover:border-red-500 transition-colors mb-4"
                data-testid="capture-area"
              >
                <Camera className="w-20 h-20 text-red-500 mb-3" strokeWidth={1.3} />
                <span className="text-lg font-medium">
                  {isProfile ? "Wählen foto" : "Kamera öffnen"}
                </span>
                <span className="text-xs text-white/50 mt-2">
                  {isProfile ? "fotocamera o album" : "o scegli dalla galleria"}
                </span>
              </label>
            )}

            <input
              id="cam-input"
              type="file"
              accept="image/*"
              {...(isProfile ? {} : { capture: "environment" })}
              onChange={onPick}
              className="hidden"
              data-testid="cam-input"
            />

            {preview && status === "uploading" && (
              <div className="mb-3">
                <div className="text-xs text-white/60 mb-1">Lädt {progress}%</div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-red-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-auto">
              {preview && (
                <button
                  onClick={reset}
                  disabled={status === "uploading"}
                  className="flex-1 py-3 border border-white/20 rounded-lg text-sm font-medium"
                  data-testid="retake-btn"
                >
                  Riprendi
                </button>
              )}
              {preview ? (
                <button
                  onClick={upload}
                  disabled={status === "uploading"}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-bold flex items-center justify-center gap-2"
                  data-testid="upload-btn"
                >
                  {status === "uploading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {status === "uploading" ? "Lädt…" : "Senden al CRM"}
                </button>
              ) : (
                <label
                  htmlFor="cam-input"
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-bold flex items-center justify-center gap-2 cursor-pointer"
                  data-testid="open-camera-btn"
                >
                  <Camera className="w-4 h-4" />
                  Scatta foto
                </label>
              )}
            </div>
          </>
        )}
      </div>

      <div className="px-5 py-3 border-t border-white/10 text-center text-[10px] text-white/40">
        Wertia Finanz GmbH · Sessione sicura
      </div>
    </div>
  );
}
